import React, { useMemo, useRef, useEffect, useState } from "react";
export default function PolyhouseDesigner({
  BASE_SCALE,
  length,
  width,
  entryStartLabel,
  footingDiaM,
  showGrid,
  showLabels,
  showOutline,
  showBC,
  showHC,
  blockName,
  showDimensions = true,
  origin = "",
}) {
  // Inputs (meters)
  const [bigColumn, setBigColumn] = useState([]);
  const [smallColumn, setSmallColumn] = useState([]);
  const [hockeyColumn, setHockeyColumn] = useState([]);
  const svgRef = useRef(null);
  const lengthM = length - 4;
  const widthM = width - 4;
  const BAY_SPACING = 4; // fixed
  const COL_SPACING = 8; // fixed
  const bufferM = 2;

  const EPS = 1e-6;
  const eq = (a, b) => Math.abs(a - b) < EPS;

  function buildAxisPositions(total, spacing) {
    const pts = [];
    let x = 0;
    const eps = 1e-9;
    while (x < total - eps) {
      pts.push(Number(x.toFixed(6)));
      x += spacing;
    }
    if (pts.length === 0 || Math.abs(pts[pts.length - 1] - total) > eps) {
      pts.push(Number(total.toFixed(6)));
    }
    return pts;
  }
  function buildInclusive(start, end, step) {
    const pts = [];
    const dir = Math.sign(end - start) || 1;
    const s = step * dir;
    const eps = 1e-9;
    if (dir > 0) {
      for (let v = start; v <= end + eps; v += s)
        pts.push(Number(v.toFixed(6)));
    } else {
      for (let v = start; v >= end - eps; v += s)
        pts.push(Number(v.toFixed(6)));
    }
    return pts;
  }

  const toLetters = (idx) => {
    let n = idx;
    let s = "";
    while (n >= 0) {
      s = String.fromCharCode((n % 26) + 65) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  };

  const { xs, ys, points, meta } = useMemo(() => {
    const L = Math.max(0.5, Number(lengthM) || 0);
    const W = Math.max(0.5, Number(widthM) || 0);
    const sx = BAY_SPACING;
    const sy = COL_SPACING;

    const xs = buildAxisPositions(L, sx);
    const ys = buildAxisPositions(W, sy);

    const pts = [];
    ys.forEach((y, yi) => {
      xs.forEach((x, xi) => {
        const row = toLetters(yi);
        const col = xi + 1;
        pts.push({ label: `${row}${col}`, x, y, col, row });
      });
    });

    const meta = {
      bayCount: xs.length,
      colLineCount: ys.length,
      liveBaySpacing: xs.length > 1 ? xs[1] - xs[0] : 0,
      liveColSpacing: ys.length > 1 ? ys[1] - ys[0] : 0,
      diagM: Math.hypot(L, W),
      L,
      W,
    };
    setSmallColumn(pts);
    return { xs, ys, points: pts, meta };
  }, [lengthM, widthM]);

  const { bcPoints, hcPoints, hcPairs, blockLabels } = useMemo(() => {
    const stepY = meta.liveColSpacing || COL_SPACING;
    const mid = stepY / 2;
    const bc = [];
    let bcIdx = 1;
    if (mid > 0 && meta.W - mid >= mid) {
      const ysMid = buildInclusive(mid, meta.W - mid, stepY);
      for (const y of ysMid) {
        for (const xEdge of [0, meta.L]) {
          bc.push({ x: xEdge, y, label: `BC${bcIdx++}`, kind: "BC" });
        }
      }
    }
    setBigColumn(bc);
    const b = Number(bufferM) || 0;
    const stepHC = 4;
    const off = 2;
    const hc = [];

    for (const x of buildInclusive(-b + off, meta.L + b - off, stepHC))
      hc.push({ x, y: -b, label: "" });
    for (const x of buildInclusive(-b + off, meta.L + b - off, stepHC))
      hc.push({ x, y: meta.W + b, label: "" });
    for (const y of buildInclusive(-b + off, meta.W + b - off, stepHC))
      hc.push({ x: -b, y, label: "" });
    for (const y of buildInclusive(-b + off, meta.W + b - off, stepHC))
      hc.push({ x: meta.L + b, y, label: "" });

    hc.forEach((p, i) => (p.label = `HC${i + 1}`));
    setHockeyColumn(hc);
    const hcPairs = [];
    const eqN = (a, b) => Math.abs(a - b) < 1e-6;
    for (let i = 0; i < hc.length - 1; i++) {
      const a = hc[i],
        bpt = hc[i + 1];
      if (eqN(a.y, -b) && eqN(bpt.y, -b) && !eqN(a.x, bpt.x))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "bottom" });
      if (eqN(a.y, meta.W + b) && eqN(bpt.y, meta.W + b) && !eqN(a.x, bpt.x))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "top" });
      if (eqN(a.x, -b) && eqN(bpt.x, -b) && !eqN(a.y, bpt.y))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "left" });
      if (eqN(a.x, meta.L + b) && eqN(bpt.x, meta.L + b) && !eqN(a.y, bpt.y))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "right" });
    }
    const blocks = [];
    for (let yi = 0; yi < ys.length - 1; yi++) {
      const y0 = ys[yi],
        y1 = ys[yi + 1];
      if (!eq(y1 - y0, COL_SPACING)) continue;
      for (let xi = 0; xi < xs.length - 1; xi++) {
        const x0 = xs[xi],
          x1 = xs[xi + 1];
        if (!eq(x1 - x0, BAY_SPACING)) continue;
        const cx = (x0 + x1) / 2;
        const cy = (y0 + y1) / 2;
        const name = `Block ${toLetters(yi)}${xi + 1}`;
        blocks.push({ x: cx, y: cy, name });
      }
    }

    return { bcPoints: bc, hcPoints: hc, hcPairs, blockLabels: blocks };
  }, [xs, ys, meta.L, meta.W, meta.liveColSpacing, bufferM]);

  const selectedPair = useMemo(
    () => hcPairs.find((p) => p.startLabel === entryStartLabel),
    [hcPairs, entryStartLabel]
  );

  // const canvasPx = { w: 980, h: 680 };
  const pad = 60;

  const { scale, canvasPx, minXM, minYM, innerHPx } = useMemo(() => {
    let extraLeftM = 0,
      extraRightM = 0,
      extraTopM = 0,
      extraBottomM = 0;
    if (selectedPair) {
      if (selectedPair.edge === "left") extraLeftM = 2;
      if (selectedPair.edge === "right") extraRightM = 2;
      if (selectedPair.edge === "top") extraTopM = 2;
      if (selectedPair.edge === "bottom") extraBottomM = 2;
    }
    const minXM = -bufferM - extraLeftM;
    const minYM = -bufferM - extraBottomM;
    const Ld = lengthM + 2 * bufferM + extraLeftM + extraRightM;
    const Wd = widthM + 2 * bufferM + extraTopM + extraBottomM;
    const scale = BASE_SCALE;
    const innerWPx = Ld * scale;
    const innerHPx = Wd * scale;
    const canvasPx = {
      w: Math.round(innerWPx + 2 * pad),
      h: Math.round(innerHPx + 2 * pad),
    };
    return { scale, canvasPx, minXM, minYM, innerHPx };
  }, [lengthM, widthM, bufferM, selectedPair, BASE_SCALE]);

  function mx(xm) {
    return pad + (xm - minXM) * scale;
  }
  function my(ym) {
    return pad + (innerHPx - (ym - minYM) * scale);
  }

  function downloadDesign() {
    const svg = svgRef.current;
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    const svgBlob = new Blob(
      [`<?xml version="1.0" encoding="UTF-8"?>\n` + source],
      { type: "image/svg+xml;charset=utf-8" }
    );
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;

    img.onload = () => {
      const img1 = new Image();
      const img2 = new Image();
      const img3 = new Image();

      img1.crossOrigin = img2.crossOrigin = img3.crossOrigin = "anonymous";

      img1.src =
        "https://res.cloudinary.com/dsmabnook/image/upload/v1757488754/12_epjdu0.png";
      img2.src =
        "https://res.cloudinary.com/dsmabnook/image/upload/v1757488755/14_z61tqt.png";
      img3.src =
        "https://res.cloudinary.com/dsmabnook/image/upload/v1757488759/13_l5o3jy.png";

      img1.onload = () => {
        img2.onload = () => {
          img3.onload = () => {
            const designPadding = 2;
            const footerHeight = 90;
            const borderWidth = 2;
            const outerBorderWidth = 2;
            const outerMargin = 10;

            // Total canvas width and scaling
            const minWidth = 1200;
            const totalWidth = Math.max(img.width, minWidth);
            const canvasWidth = totalWidth + outerMargin * 2;
            const foundationWidth = totalWidth * 0.75;
            const rightWidth = totalWidth * 0.25;

            const scaleFactor = foundationWidth / img.width;
            const foundationHeight = img.height * scaleFactor;

            const scale1 = rightWidth / img1.width;
            const scale2 = rightWidth / img2.width;
            const scale3 = rightWidth / img3.width;

            const img1Height = img1.height * scale1;
            const img2Height = img2.height * scale2;
            const img3Height = img3.height * scale3;

            const textBoxHeight = 100;
            const titleBoxHeight = 40;
            const totalRightHeight =
              img1Height +
              img2Height +
              img3Height +
              textBoxHeight +
              titleBoxHeight;

            const canvasHeight =
              Math.max(foundationHeight, totalRightHeight) +
              footerHeight +
              outerMargin * 2 +
              designPadding * 2;

            const canvas = document.createElement("canvas");
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const ctx = canvas.getContext("2d");

            // Background
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Outer border
            ctx.strokeStyle = "#000";
            ctx.lineWidth = outerBorderWidth;
            ctx.strokeRect(
              outerMargin + outerBorderWidth / 2,
              outerMargin + outerBorderWidth / 2,
              totalWidth - outerBorderWidth,
              canvasHeight - outerMargin * 2 - outerBorderWidth
            );

            // Draw foundation
            const foundationY =
              outerMargin +
              designPadding +
              (canvasHeight -
                footerHeight -
                designPadding * 2 -
                foundationHeight -
                outerMargin * 2) /
                2;
            ctx.drawImage(
              img,
              outerMargin + designPadding,
              foundationY,
              foundationWidth,
              foundationHeight
            );

            // Draw watermark logo
            const logo = new Image();
            logo.crossOrigin = "anonymous";
            logo.src =
              "https://res.cloudinary.com/dsmabnook/image/upload/v1758885255/kryzen_logo_2_5_t5jwwe.png";
            // logo.src =
            //   "https://res.cloudinary.com/dsmabnook/image/upload/v1757651285/kryzenLogo_v87dtw.png";
            logo.onload = () => {
              const maxDim = Math.min(foundationWidth, foundationHeight) * 0.6;
              const s = maxDim / Math.max(logo.width, logo.height);
              const lw = logo.width * s;
              const lh = logo.height * s;

              ctx.save();
              ctx.translate(
                outerMargin + designPadding + foundationWidth / 2,
                foundationY + foundationHeight / 2
              );
              ctx.rotate((-45 * Math.PI) / 180);
              ctx.globalAlpha = 0.08;
              ctx.drawImage(logo, -lw / 2, -lh / 2, lw, lh);
              ctx.restore();
              ctx.globalAlpha = 1.0;

              // Right column start
              const startX = outerMargin + designPadding + foundationWidth;
              const footerY = canvasHeight - footerHeight - outerMargin;
              let startY = footerY - totalRightHeight;

              // Title box
              ctx.lineWidth = borderWidth;
              ctx.strokeRect(
                startX,
                startY,
                rightWidth - designPadding,
                titleBoxHeight
              );
              ctx.font = "bold 14px Arial";
              ctx.fillStyle = "#000";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(
                `Polyhouse foundation: (${length}m X ${width}m)`,
                startX + rightWidth / 2 - designPadding / 2,
                startY + titleBoxHeight / 2
              );
              startY += titleBoxHeight;

              // Right column images
              [img1, img2, img3].forEach((sideImg, i) => {
                const sideHeight = [img1Height, img2Height, img3Height][i];
                ctx.drawImage(
                  sideImg,
                  startX,
                  startY,
                  rightWidth - designPadding,
                  sideHeight
                );
                ctx.strokeRect(
                  startX,
                  startY,
                  rightWidth - designPadding,
                  sideHeight
                );
                startY += sideHeight;
              });

              // Text box
              ctx.strokeRect(
                startX,
                startY,
                rightWidth - designPadding,
                textBoxHeight
              );
              ctx.font = "14px Arial";
              ctx.fillStyle = "#111";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";

              const textBoxCenterX = startX + (rightWidth - designPadding) / 2;
              const textBoxCenterY = startY + textBoxHeight / 2;

              const labels = [
                `Big Columns (BC): ${formatLabels(bigColumn)}`,
                `Hockey Cols (HC): ${formatLabels(hockeyColumn)}`,
                `Small Columns (SC): ${formatLabels(smallColumn)}`,
              ];

              const lineHeight = 20;
              let currentY =
                textBoxCenterY -
                (labels.length * lineHeight) / 2 +
                lineHeight / 2;
              labels.forEach((line) => {
                ctx.fillText(line, textBoxCenterX, currentY);
                currentY += lineHeight;
              });

              // Footer (Left + Right)
              const footerX = outerMargin;
              const footerWidth = canvas.width - outerMargin * 2;

              // LEFT 75% - logo + copyright
              const leftWidth = footerWidth * 0.75;
              ctx.strokeRect(footerX, footerY, leftWidth, footerHeight);

              const footerLogo = new Image();
              footerLogo.crossOrigin = "anonymous";
              footerLogo.src =
                "https://res.cloudinary.com/dsmabnook/image/upload/v1757651285/kryzenLogo_v87dtw.png";

              footerLogo.onload = () => {
                const logoH = footerHeight;
                const logoW = (footerLogo.width / footerLogo.height) * logoH;
                const logoX = footerX;
                const logoY = footerY;

                ctx.lineWidth = borderWidth;
                ctx.strokeRect(logoX, logoY, logoW, logoH);
                ctx.drawImage(footerLogo, logoX, logoY, logoW, logoH);

                const textXCenter = footerX + logoW + (leftWidth - logoW) / 2;
                ctx.font = "13px Arial";
                ctx.fillStyle = "#333";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                const copyrightText =
                  "Copyright, 2018-2025 Kryzen Biotech Private Limited. All designs and their accessories, fittings and connections are copyrighted material of Kryzen Biotech Private Limited. Using the design and diagrams without the written consent of Kryzen Biotech is a copyright offence.";

                const maxTextWidth = leftWidth - logoW - 10;
                const words = copyrightText.split(" ");
                let line = "";
                let lines = [];
                words.forEach((word) => {
                  const testLine = line + word + " ";
                  if (ctx.measureText(testLine).width > maxTextWidth) {
                    lines.push(line);
                    line = word + " ";
                  } else {
                    line = testLine;
                  }
                });
                lines.push(line);

                let textStartY =
                  footerY + footerHeight / 2 - (lines.length * 16) / 2;
                lines.forEach((l) => {
                  ctx.fillText(l, textXCenter, textStartY);
                  textStartY += 16;
                });

                // RIGHT 25% - contact info
                const rightWidth = footerWidth * 0.25;
                const rightX = footerX + leftWidth;
                ctx.strokeRect(rightX, footerY, rightWidth, footerHeight);

                ctx.font = "13px Arial";
                ctx.fillStyle = "#000";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                const contactLines = [
                  "www.kryzen.com",
                  "contact@kryzen.com",
                  "+91-9870424425",
                ];
                const contactSpacing = footerHeight / (contactLines.length + 1);

                contactLines.forEach((line, idx) => {
                  ctx.fillText(
                    line,
                    rightX + rightWidth / 2,
                    footerY + contactSpacing * (idx + 1)
                  );
                });
                // Export PNG
                // ⚡ COMPRESSED EXPORT - lower quality PNG (or JPEG)
                const exportQuality = 0.8;
                canvas.toBlob(
                  (blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `polyhouse-foundation (${length}mX${width}m)_compressed.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  },
                  "image/jpeg",
                  exportQuality
                );
              };
            };
          };
        };
      };
    };

    function formatLabels(points) {
      if (!points || points.length === 0) return "";
      if (points.length <= 4) return points.map((p) => p.label).join(", ");
      const firstThree = points.slice(0, 3).map((p) => p.label);
      const last = points[points.length - 1].label;
      return [...firstThree, "...", last].join(", ");
    }
  }

  // function downloadDesign() {
  //   const svg = svgRef.current;
  //   if (!svg) return;

  //   const serializer = new XMLSerializer();
  //   const source = serializer.serializeToString(svg);
  //   const svgBlob = new Blob(
  //     [`<?xml version="1.0" encoding="UTF-8"?>\n` + source],
  //     { type: "image/svg+xml;charset=utf-8" }
  //   );
  //   const url = URL.createObjectURL(svgBlob);

  //   const img = new Image();
  //   img.crossOrigin = "anonymous";
  //   img.src = url;

  //   img.onload = () => {
  //     const img1 = new Image();
  //     const img2 = new Image();
  //     const img3 = new Image();
  //     img1.crossOrigin = img2.crossOrigin = img3.crossOrigin = "anonymous";

  //     img1.src =
  //       "https://res.cloudinary.com/dsmabnook/image/upload/v1757488754/12_epjdu0.png";
  //     img2.src =
  //       "https://res.cloudinary.com/dsmabnook/image/upload/v1757488755/14_z61tqt.png";
  //     img3.src =
  //       "https://res.cloudinary.com/dsmabnook/image/upload/v1757488759/13_l5o3jy.png";

  //     img1.onload = () => {
  //       img2.onload = () => {
  //         img3.onload = () => {
  //           const designPadding = 2;
  //           const footerHeight = 90;
  //           const borderWidth = 2;
  //           const outerBorderWidth = 2;
  //           const outerMargin = 10;

  //           const totalWidth = Math.max(img.width, 1200);
  //           const foundationWidth = totalWidth * 0.75;
  //           const scaleFactor = foundationWidth / img.width;
  //           const foundationHeight = img.height * scaleFactor;

  //           const rightWidth = totalWidth * 0.25;
  //           const scale1 = rightWidth / img1.width;
  //           const scale2 = rightWidth / img2.width;
  //           const scale3 = rightWidth / img3.width;
  //           const img1Height = img1.height * scale1;
  //           const img2Height = img2.height * scale2;
  //           const img3Height = img3.height * scale3;

  //           const textBoxHeight = 100;
  //           const titleBoxHeight = 40;
  //           const totalRightHeight =
  //             img1Height +
  //             img2Height +
  //             img3Height +
  //             textBoxHeight +
  //             titleBoxHeight;

  //           const canvasHeight =
  //             Math.max(foundationHeight, totalRightHeight) +
  //             footerHeight +
  //             outerMargin * 2 +
  //             designPadding * 2;

  //           // 🧠 reduce export size by 0.75x scale
  //           const scaleDown = 0.75;
  //           const canvas = document.createElement("canvas");
  //           canvas.width = (totalWidth + outerMargin * 2) * scaleDown;
  //           canvas.height = canvasHeight * scaleDown;
  //           const ctx = canvas.getContext("2d");
  //           ctx.scale(scaleDown, scaleDown);

  //           // Background
  //           ctx.fillStyle = "#fff";
  //           ctx.fillRect(
  //             0,
  //             0,
  //             canvas.width / scaleDown,
  //             canvas.height / scaleDown
  //           );

  //           // Outer border
  //           ctx.strokeStyle = "#000";
  //           ctx.lineWidth = outerBorderWidth;
  //           ctx.strokeRect(
  //             outerMargin + outerBorderWidth / 2,
  //             outerMargin + outerBorderWidth / 2,
  //             totalWidth - outerMargin * 2 - outerBorderWidth,
  //             canvasHeight - outerMargin * 2 - outerBorderWidth
  //           );

  //           const foundationY =
  //             outerMargin +
  //             designPadding +
  //             (canvasHeight -
  //               footerHeight -
  //               designPadding * 2 -
  //               foundationHeight -
  //               outerMargin * 2) /
  //               2;

  //           ctx.drawImage(
  //             img,
  //             outerMargin + designPadding,
  //             foundationY,
  //             foundationWidth,
  //             foundationHeight
  //           );

  //           const logo = new Image();
  //           logo.crossOrigin = "anonymous";
  //           logo.src =
  //             "https://res.cloudinary.com/dsmabnook/image/upload/v1758885255/kryzen_logo_2_5_t5jwwe.png";
  //           logo.onload = () => {
  //             const maxDim = Math.min(foundationWidth, foundationHeight) * 0.6;
  //             const s = maxDim / Math.max(logo.width, logo.height);
  //             const lw = logo.width * s;
  //             const lh = logo.height * s;

  //             ctx.save();
  //             ctx.translate(
  //               outerMargin + designPadding + foundationWidth / 2,
  //               foundationY + foundationHeight / 2
  //             );
  //             ctx.rotate((-45 * Math.PI) / 180);
  //             ctx.globalAlpha = 0.08;
  //             ctx.drawImage(logo, -lw / 2, -lh / 2, lw, lh);
  //             ctx.restore();
  //             ctx.globalAlpha = 1.0;

  //             const startX = outerMargin;
  //             const footerY = canvasHeight - footerHeight - outerMargin;
  //             let startY = footerY - totalRightHeight;

  //             // Title box
  //             ctx.lineWidth = borderWidth;
  //             ctx.strokeRect(
  //               startX + foundationWidth,
  //               startY,
  //               rightWidth,
  //               titleBoxHeight
  //             );
  //             ctx.font = "bold 14px Arial";
  //             ctx.fillStyle = "#000";
  //             ctx.textAlign = "center";
  //             ctx.textBaseline = "middle";
  //             ctx.fillText(
  //               `Polyhouse foundation: (${length}m X ${width}m)`,
  //               startX + foundationWidth + rightWidth / 2,
  //               startY + titleBoxHeight / 2
  //             );
  //             startY += titleBoxHeight;

  //             [img1, img2, img3].forEach((sideImg, i) => {
  //               const sideHeight = [img1Height, img2Height, img3Height][i];
  //               ctx.drawImage(
  //                 sideImg,
  //                 startX + foundationWidth,
  //                 startY,
  //                 rightWidth,
  //                 sideHeight
  //               );
  //               ctx.lineWidth = borderWidth;
  //               ctx.strokeRect(
  //                 startX + foundationWidth,
  //                 startY,
  //                 rightWidth,
  //                 sideHeight
  //               );
  //               startY += sideHeight;
  //             });

  //             const textBoxY = startY;
  //             ctx.strokeRect(
  //               startX + foundationWidth,
  //               textBoxY,
  //               rightWidth,
  //               textBoxHeight
  //             );

  //             ctx.font = "14px Arial";
  //             ctx.fillStyle = "#111";
  //             ctx.textAlign = "center";
  //             ctx.textBaseline = "middle";

  //             const textBoxCenterX = startX + foundationWidth + rightWidth / 2;
  //             const textBoxCenterY = textBoxY + textBoxHeight / 2;

  //             const labels = [
  //               `Big Columns (BC): ${formatLabels(bigColumn)}`,
  //               `Hockey Cols (HC): ${formatLabels(hockeyColumn)}`,
  //               `Small Columns (SC): ${formatLabels(smallColumn)}`,
  //             ];

  //             const lineHeight = 20;
  //             const totalTextHeight = labels.length * lineHeight;
  //             let currentY =
  //               textBoxCenterY - totalTextHeight / 2 + lineHeight / 2;
  //             labels.forEach((line) => {
  //               ctx.fillText(line, textBoxCenterX, currentY);
  //               currentY += lineHeight;
  //             });

  //             const footerX = outerMargin;
  //             const footerWidth = totalWidth - outerMargin * 2;
  //             const leftWidth = footerWidth * 0.75;
  //             ctx.strokeRect(footerX, footerY, leftWidth, footerHeight);

  //             const footerLogo = new Image();
  //             footerLogo.crossOrigin = "anonymous";
  //             footerLogo.src =
  //               "https://res.cloudinary.com/dsmabnook/image/upload/v1757651285/kryzenLogo_v87dtw.png";
  //             footerLogo.onload = () => {
  //               const logoH = footerHeight;
  //               const logoW = (footerLogo.width / footerLogo.height) * logoH;
  //               const logoX = footerX;
  //               const logoY = footerY;

  //               ctx.lineWidth = borderWidth;
  //               ctx.strokeRect(logoX, logoY, logoW, logoH);
  //               ctx.drawImage(footerLogo, logoX, logoY, logoW, logoH);

  //               const textXCenter = footerX + logoW + (leftWidth - logoW) / 2;
  //               ctx.font = "13px Arial";
  //               ctx.fillStyle = "#333";
  //               ctx.textAlign = "center";
  //               ctx.textBaseline = "middle";

  //               const copyrightText =
  //                 "Copyright, 2018-2025 Kryzen Biotech Private Limited. All designs and their accessories, fittings and connections are copyrighted material of Kryzen Biotech Private Limited. Using the design and diagrams without the written consent of Kryzen Biotech is a copyright offence.";

  //               const maxTextWidth = leftWidth - logoW - 10;
  //               const words = copyrightText.split(" ");
  //               let line = "";
  //               let lines = [];
  //               words.forEach((word) => {
  //                 const testLine = line + word + " ";
  //                 if (ctx.measureText(testLine).width > maxTextWidth) {
  //                   lines.push(line);
  //                   line = word + " ";
  //                 } else {
  //                   line = testLine;
  //                 }
  //               });
  //               lines.push(line);

  //               let textStartY =
  //                 footerY + footerHeight / 2 - (lines.length * 16) / 2;
  //               lines.forEach((l) => {
  //                 ctx.fillText(l, textXCenter, textStartY);
  //                 textStartY += 16;
  //               });

  //               const rightWidth = footerWidth * 0.25;
  //               const rightX = footerX + leftWidth;
  //               ctx.strokeRect(rightX, footerY, rightWidth, footerHeight);
  //               ctx.font = "13px Arial";
  //               ctx.fillStyle = "#000";
  //               ctx.textAlign = "center";
  //               ctx.textBaseline = "middle";

  //               const contactLines = [
  //                 "www.kryzen.com",
  //                 "contact@kryzen.com",
  //                 "+91-9870424425",
  //               ];
  //               const contactSpacing = footerHeight / (contactLines.length + 1);

  //               contactLines.forEach((line, idx) => {
  //                 ctx.fillText(
  //                   line,
  //                   rightX + rightWidth / 2,
  //                   footerY + contactSpacing * (idx + 1)
  //                 );
  //               });

  //               // ⚡ COMPRESSED EXPORT - lower quality PNG (or JPEG)
  //               const exportQuality = 0.8;
  //               canvas.toBlob(
  //                 (blob) => {
  //                   const url = URL.createObjectURL(blob);
  //                   const a = document.createElement("a");
  //                   a.href = url;
  //                   a.download = `polyhouse-foundation (${length}mX${width}m)_compressed.png`;
  //                   document.body.appendChild(a);
  //                   a.click();
  //                   document.body.removeChild(a);
  //                   URL.revokeObjectURL(url);
  //                 },
  //                 "image/jpeg",
  //                 exportQuality
  //               );
  //             };
  //           };
  //         };
  //       };
  //     };
  //   };

  //   function formatLabels(points) {
  //     if (!points || points.length === 0) return "";
  //     if (points.length <= 4) return points.map((p) => p.label).join(", ");
  //     const firstThree = points.slice(0, 3).map((p) => p.label);
  //     const last = points[points.length - 1].label;
  //     return [...firstThree, "...", last].join(", ");
  //   }
  // }

  const footingPx = Math.max(3, (footingDiaM * scale) / 2);

  useEffect(() => {
    try {
      const xsTest = buildAxisPositions(40, 4);
      const ysTest = buildAxisPositions(24, 8);
      console.assert(xsTest.length === 11, `Expected 11 columns`);
      console.assert(ysTest.length === 4, `Expected 4 rows`);
    } catch (e) {}
  }, []);

  function getEntryRect() {
    if (!selectedPair) return null;
    const a = hcPoints[selectedPair.i];
    const b = hcPoints[selectedPair.j];
    if (!a || !b) return null;

    if (eq(a.y, b.y)) {
      const yEdge = a.y; // -b (bottom) or W+b (top)
      const x0 = Math.min(a.x, b.x);
      const w = Math.abs(b.x - a.x); // 4 m
      const h = 4;
      const y0 = yEdge - 2; // center around border
      return { x0, y0, w, h };
    } else if (eq(a.x, b.x)) {
      const xEdge = a.x; // -b (left) or L+b (right)
      const y0 = Math.min(a.y, b.y);
      const h = Math.abs(b.y - a.y); // 4 m
      const w = 4;
      const x0 = xEdge - 2; // center around border
      return { x0, y0, w, h };
    }
    return null;
  }

  const entryRect = getEntryRect();

  function edgeOfHC(p) {
    const b = Number(bufferM) || 0;
    if (eq(p.y, -b)) return "bottom";
    if (eq(p.y, meta.W + b)) return "top";
    if (eq(p.x, -b)) return "left";
    return "right";
  }

  return (
    <div className={!origin && "w-full h-full p-4 text-sm"}>
      <h3>Polyhouse Foundation Design</h3>
      <h4>{blockName?.join(" + ").toUpperCase()}</h4>
      <div className="bg-white rounded-2xl shadow p-4 border overflow-auto">
        <div
          style={{
            minWidth: "100%",
            width: canvasPx.w + scale * 4,
          }}
        >
          <svg
            id="svgImage"
            ref={svgRef}
            width="100%"
            height={canvasPx.h}
            viewBox={`0 0 ${canvasPx.w} ${canvasPx.h}`}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Background */}
            <rect
              x={0}
              y={0}
              width={canvasPx.w}
              height={canvasPx.h}
              fill="#ffffff"
            />

            {/* Hockey Area (buffer each side) */}
            <rect
              x={mx(-bufferM)}
              y={my(widthM + bufferM)}
              width={(lengthM + 2 * bufferM) * scale}
              height={(widthM + 2 * bufferM) * scale}
              fill="#e0f2fe"
              fillOpacity={0.25}
              stroke="#0ea5e9"
              strokeWidth={1.5}
            />

            {/* Outline */}
            {showOutline && (
              <rect
                x={mx(0)}
                y={my(widthM)}
                width={lengthM * scale}
                height={widthM * scale}
                fill="none"
                stroke="#111"
                strokeWidth={1.5}
              />
            )}

            {/* Grid lines */}
            {showGrid && (
              <g>
                {/* Vertical grid lines (bays) */}
                {xs.map((x, i) => (
                  <line
                    key={`vx-${i}`}
                    x1={mx(x)}
                    y1={my(0)}
                    x2={mx(x)}
                    y2={my(widthM)}
                    stroke="#e5e7eb"
                    strokeWidth={1}
                  />
                ))}
                {/* Horizontal grid lines (column lines) */}
                {ys.map((y, i) => (
                  <line
                    key={`hy-${i}`}
                    x1={mx(0)}
                    y1={my(y)}
                    x2={mx(lengthM)}
                    y2={my(y)}
                    stroke="#e5e7eb"
                    strokeWidth={1}
                  />
                ))}
              </g>
            )}

            {/* Footings (foundation grid) - black */}
            <g>
              {points.map((p, idx) => (
                <g key={`f-${idx}`}>
                  <circle
                    cx={mx(p.x)}
                    cy={my(p.y)}
                    r={footingPx}
                    fill="#111827"
                    opacity={0.95}
                  />
                  {showLabels && (
                    <text
                      x={mx(p.x) + footingPx + 3}
                      y={my(p.y) - footingPx - 3}
                      fontSize={scale / 2.3}
                      fill="#111827"
                    >
                      {p.label}
                    </text>
                  )}
                </g>
              ))}
            </g>

            {/* Block names (centers of full 4×8 rectangles) */}
            {showLabels && (
              <g>
                {blockLabels.map((b, i) => (
                  <>
                    {i === 0 && (
                      <>
                        <line
                          x1={mx(b.x - 2)}
                          y1={my(b.y + 4)}
                          x2={mx(b.x) + scale * 2}
                          y2={my(b.y + 4)}
                          stroke="#ba7b0d"
                          strokeDasharray="4"
                        />
                        <text
                          y={my(b.y + 3.5)}
                          x={mx(b.x)}
                          fontSize={scale / 2}
                          textAnchor="middle"
                          fill={"#ba7b0d"}
                          fontWeight={"bold"}
                        >
                          {4} meters
                        </text>

                        <text
                          y={my(b.y)}
                          x={mx(b.x) + scale * 1.6}
                          writingMode="vertical-rl"
                          textAnchor="middle"
                          fill={"#ba7b0d"}
                          fontSize={scale / 2}
                          fontWeight={"bold"}
                        >
                          {8} meters
                        </text>
                        <line
                          x1={mx(b.x + 2)}
                          y1={my(b.y - 4)}
                          x2={mx(b.x) + scale * 2}
                          y2={my(b.y + 4)}
                          stroke="#ba7b0d"
                          strokeDasharray="4"
                        />
                      </>
                    )}

                    <text
                      key={`blk-${i}`}
                      x={mx(b.x)}
                      y={my(b.y) + 3}
                      fontSize={scale / 2.5}
                      textAnchor="middle"
                      fill="#374151"
                    >
                      {b.name}
                    </text>
                  </>
                ))}
              </g>
            )}

            {/* BC points (Big Column) */}
            {showBC && (
              <g>
                {bcPoints.map((p, i) => (
                  <g key={`bc-${i}`}>
                    <circle
                      cx={mx(p.x)}
                      cy={my(p.y)}
                      r={Math.max(3, footingPx * 0.7)}
                      fill="#1c8beb"
                      stroke="#111827"
                      strokeWidth={0.8}
                    />
                    {showLabels && (
                      <text
                        x={mx(p.x) + footingPx + 3}
                        y={my(p.y) + 11}
                        fontSize={scale / 2}
                        fill="#4015c2"
                      >
                        {p.label}
                      </text>
                    )}
                  </g>
                ))}
              </g>
            )}

            {/* HC points (hockey border, 4m, first at 2m from each corner) */}
            {showHC && (
              <g>
                {/* HC points */}
                {hcPoints.map((p, i) => {
                  const edge = edgeOfHC(p);
                  let tx = mx(p.x),
                    ty = my(p.y),
                    anchor = "middle"; //"start" | "middle" | "end" =
                  if (edge === "bottom") {
                    ty = my(p.y) + 14;
                    anchor = "middle";
                  }
                  if (edge === "top") {
                    ty = my(p.y) - 6;
                    anchor = "middle";
                  }
                  if (edge === "left") {
                    tx = mx(p.x) - (footingPx + 8);
                    anchor = "end";
                    ty = my(p.y) + 3;
                  }
                  if (edge === "right") {
                    tx = mx(p.x) + (footingPx + 8);
                    anchor = "start";
                    ty = my(p.y) + 3;
                  }
                  return (
                    <g key={`hc-${i}`}>
                      <rect
                        x={mx(p.x) - Math.max(3, footingPx * 0.6)}
                        y={my(p.y) - Math.max(3, footingPx * 0.6)}
                        width={Math.max(6, footingPx * 1.2)}
                        height={Math.max(6, footingPx * 1.2)}
                        fill="#1cebad"
                        stroke="#0c4a6e"
                        strokeWidth={0.8}
                      />
                      {showLabels && (
                        <text
                          x={tx}
                          y={ty}
                          fontSize={scale / 2}
                          fill="#057a57"
                          textAnchor={anchor}
                        >
                          {p.label}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Corner join lines */}
                {/* bottom-left */}
                <line
                  x1={mx(-bufferM + 2)}
                  y1={my(-bufferM)}
                  x2={mx(-bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(-bufferM)}
                  y1={my(-bufferM + 2)}
                  x2={mx(-bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* bottom-right */}
                <line
                  x1={mx(lengthM + bufferM - 2)}
                  y1={my(-bufferM)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(lengthM + bufferM)}
                  y1={my(-bufferM + 2)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* top-left */}
                <line
                  x1={mx(-bufferM + 2)}
                  y1={my(widthM + bufferM)}
                  x2={mx(-bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(-bufferM)}
                  y1={my(widthM + bufferM - 2)}
                  x2={mx(-bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* top-right */}
                <line
                  x1={mx(lengthM + bufferM - 2)}
                  y1={my(widthM + bufferM)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(lengthM + bufferM)}
                  y1={my(widthM + bufferM - 2)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
              </g>
            )}

            {/* Entry Cabin (4×4 m) between HCn and HC(n+1), 2 m inside from hockey border) */}
            {entryRect && (
              <>
                <rect
                  x={mx(entryRect.x0)}
                  y={my(entryRect.y0 + entryRect.h)}
                  width={entryRect.w * scale}
                  height={entryRect.h * scale}
                  fill="#10b981"
                  fillOpacity={0.6}
                  stroke="#065f46"
                  strokeWidth={1.2}
                />
                <text
                  x={mx(entryRect.x0) + (entryRect.w * scale) / 2}
                  y={my(entryRect.y0 + entryRect.h) + (entryRect.h * scale) / 2}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fontSize={scale / 2}
                  fontWeight="bold"
                  fill="#ba7b0d"
                >
                  Entry Cabin
                </text>
              </>
            )}

            {/* Axes labels (no overall L/W text on drawing) */}
            {showLabels && (
              <g fontSize={scale / 2.2} fill="#111827">
                {/* X axis labels (bottom) */}
                {xs.map((x, xi) => (
                  <text
                    key={`xl-${xi}`}
                    x={mx(x)}
                    y={my(0) + 18}
                    textAnchor="middle"
                  >
                    {xi + 1}
                  </text>
                ))}
                {/* Y axis labels (left) */}
                {ys.map((y, yi) => (
                  <text
                    key={`yl-${yi}`}
                    x={mx(0) - 18}
                    y={my(y) + 3}
                    textAnchor="end"
                  >
                    {toLetters(yi)}
                  </text>
                ))}
              </g>
            )}
            {/* Overall Dimensions */}
            {showDimensions && (
              <g
                className="dimensions"
                fontSize={scale / 2}
                fontWeight={"bold"}
                fill="#ba7b0d"
              >
                {/* Width (Y direction, right side) */}
                <line
                  x1={mx(lengthM) + scale}
                  y1={my(0)}
                  x2={mx(lengthM) + scale}
                  y2={my(widthM)}
                  stroke="#ba7b0d"
                  strokeDasharray="4"
                />
                <text
                  x={mx(lengthM) + scale * 1.5}
                  y={my(widthM / 2)}
                  writingMode="vertical-rl"
                  textAnchor="middle"
                >
                  {widthM} meters
                </text>

                {/* Length (X direction, top side) */}
                <line
                  x1={mx(0)}
                  y1={my(-1)}
                  x2={mx(lengthM)}
                  y2={my(-1)}
                  stroke="#ba7b0d"
                  strokeDasharray="4"
                />
                <text x={mx(lengthM / 2)} y={my(-1.5)} textAnchor="middle">
                  {lengthM} meters
                </text>
              </g>
            )}
            {showDimensions && (
              <g
                className="dimensions"
                fontSize={scale / 2}
                fontWeight={"bold"}
                fill="#ba7b0d"
              >
                {/* Width (Y direction, right side) */}
                <line
                  x1={mx(0) - scale}
                  y1={my(-2)}
                  x2={mx(0) - scale}
                  y2={my(widthM + 2)}
                  stroke="#ba7b0d"
                  strokeDasharray="4"
                />
                <text
                  x={mx(0) - scale * 1.4}
                  y={my(widthM / 2)}
                  writingMode="vertical-rl"
                  textAnchor="middle"
                >
                  {widthM + 4} meters
                </text>

                {/* Length (X direction, top side) */}
                <line
                  x1={mx(-2)}
                  y1={my(-3)}
                  x2={mx(lengthM + 2)}
                  y2={my(-3)}
                  stroke="#ba7b0d"
                  strokeDasharray="4"
                />
                <text x={mx(lengthM / 2)} y={my(-3.5)} textAnchor="middle">
                  {lengthM + 4} meters
                </text>
              </g>
            )}
          </svg>
        </div>
        {/* Bottom meta text removed per request */}
      </div>
      <button className="btn btn-primary m-2 btn-sm" onClick={downloadDesign}>
        Download Design
      </button>
    </div>
  );
}
